import { useState } from 'react'
import type { JSX } from 'react'

import {
  Flex,
  PencilIcon,
  IconButton,
  Text,
  TrashIcon,
  Card,
} from '@buffer-mono/popcorn'

import { useHasOrganizationAction } from '~publish/contexts/AccountContext'
import type { SavedReply } from '~publish/pages/Community/types'
import { useAddSavedReplyForm } from '~publish/pages/Community/hooks/savedReplies/useSavedReplyForm'
import { DiscardDeleteSavedReplyDialog } from '~publish/pages/Community/components/SavedReplyForm/DiscardDeleteSavedReplyDialog'
import { SavedReplyForm } from '~publish/pages/Community/components/SavedReplyForm/SavedReplyForm'

import styles from './SavedRepliesSettings.module.css'

export const SavedRepliesList = ({
  savedReplies,
}: {
  savedReplies: SavedReply[]
}): JSX.Element => {
  return (
    <Flex direction="column" gap="xs" fullWidth>
      {savedReplies.map((savedReply) => (
        <SavedReplyItem key={savedReply.id} savedReply={savedReply} />
      ))}
    </Flex>
  )
}

/**
 *
 * SAVED REPLY ITEM: Title and Text and Actions
 *
 */
const SavedReplyItem = ({
  savedReply,
}: {
  savedReply: SavedReply
}): JSX.Element => {
  const canManageSavedReplies = useHasOrganizationAction('manageSavedReplies')
  const [editOpen, setEditOpen] = useState(false)
  return (
    <>
      {!editOpen && (
        <Card
          key={savedReply.id}
          padding="md"
          className={styles.savedReplyItem}
        >
          <Flex
            direction="row"
            gap="2xs"
            align="center"
            justify="between"
            fullWidth
            className={styles.savedReplyItem}
          >
            <SavedReplyContent savedReply={savedReply} />
            {canManageSavedReplies && (
              <SavedReplyActions
                savedReply={savedReply}
                setEditOpen={setEditOpen}
              />
            )}
          </Flex>
        </Card>
      )}
      {/* Edit form */}
      <SavedReplyForm
        edit={true}
        open={editOpen}
        setOpen={setEditOpen}
        savedReply={{ ...savedReply, title: savedReply.title ?? null }}
      />
    </>
  )
}

/**
 *
 * CONTENT: Title and Text
 */
const SavedReplyContent = ({
  savedReply,
}: {
  savedReply: SavedReply
}): JSX.Element => {
  return (
    <Flex direction="column" gap="2xs" className={styles.savedReplyContent}>
      <Text size="md" weight="bold" className={styles.title} truncate>
        {savedReply.title}
      </Text>
      <Text className={styles.content} truncate>
        {savedReply.content.text}
      </Text>
    </Flex>
  )
}

/**
 *
 * ACTIONS: Edit and Delete
 */
const SavedReplyActions = ({
  savedReply,
  setEditOpen,
}: {
  savedReply: SavedReply
  setEditOpen: (open: boolean) => void
}): JSX.Element => {
  const { onDelete } = useAddSavedReplyForm({ isEditMode: false })
  const [openDiscardDialog, setOpenDiscardDialog] = useState(false)
  const savedReplyName = savedReply.title || savedReply.content.text

  return (
    <>
      <Flex direction="row" gap="2xs">
        <IconButton
          label={`Edit ${savedReplyName}`}
          tooltip="Edit saved reply"
          variant="tertiary"
          onClick={(): void => setEditOpen(true)}
        >
          <PencilIcon />
        </IconButton>
        <IconButton
          label={`Delete ${savedReplyName}`}
          tooltip="Delete saved reply"
          variant="critical-subtle"
          onClick={(): void => setOpenDiscardDialog(true)}
        >
          <TrashIcon />
        </IconButton>
      </Flex>
      <DiscardDeleteSavedReplyDialog
        open={openDiscardDialog}
        onOpenChange={setOpenDiscardDialog}
        savedReply={{ ...savedReply, title: savedReply.title ?? null }}
        onCancelDeleting={(): void => setOpenDiscardDialog(false)}
        onKeepDeleting={(): Promise<void> => onDelete(savedReply.id)}
      />
    </>
  )
}
